<script setup>
import Card from '@/Components/Card/Card.vue';
import { inject } from 'vue';
import ExplainScorePart from '@/Pages/DomainScore/Partials/DetailedReport/ExplainScorePart.vue';
import ActionType from '@/Common/Enum/ActionType.js';
import LinkDomainAnalysis from '@/Pages/DomainScore/Partials/DetailedReport/LinkDomainAnalysis.vue';
import DetailedReportHeader from '@/Pages/DomainScore/Partials/DetailedReport/DetailedReportHeader.vue';
import { getBimiDnsRecordStatus } from '@/Common/DnsRecordMessaging.js';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const result = inject('score');

const imageMessage = (() => {
  if (result.records.bimi.has_valid_logo) {
    return {
      message: t(
        'domain_score.partials.detailed_report.branding.bimi_card.valid_bimi_image'
      ),
      actionType: ActionType.NO_ACTION
    };
  }

  if (result.records.bimi.has_logo) {
    return {
      message: t(
        'domain_score.partials.detailed_report.branding.bimi_card.image_with_errors'
      ),
      actionType: ActionType.ACTION_RECOMMENDED
    };
  }

  return {
    message: t(
      'domain_score.partials.detailed_report.branding.bimi_card.image_not_found'
    ),
    actionType: ActionType.ACTION_REQUIRED
  };
})();

const certificateMessage = (() => {
  if (result.records.bimi.has_valid_certificate) {
    return {
      message: t(
        'domain_score.partials.detailed_report.branding.bimi_card.valid_cert'
      ),
      actionType: ActionType.NO_ACTION
    };
  }

  if (result.records.bimi.has_certificate) {
    return {
      message: t(
        'domain_score.partials.detailed_report.branding.bimi_card.cert_contains_errors'
      ),
      actionType: ActionType.ACTION_RECOMMENDED
    };
  }

  return {
    message: t(
      'domain_score.partials.detailed_report.branding.bimi_card.no_valid_cert'
    ),
    actionType: ActionType.ACTION_REQUIRED
  };
})();

const bimiRecordStatus = getBimiDnsRecordStatus(result);
</script>

<template>
  <Card class="bg-white-opacity mt-4">
    <DetailedReportHeader
      title="BIMI"
      :description="
        $t(
          'domain_score.partials.detailed_report.branding.bimi_card.brand_indicator_for'
        )
      "
      :tooltip="
        $t(
          'domain_score.partials.detailed_report.branding.bimi_card.bimi_record_works'
        )
      "
    />
    <div class="mt-5">
      <ExplainScorePart
        :title="
          $t(
            'domain_score.partials.detailed_report.branding.bimi_card.dns_record'
          )
        "
        :action-type="bimiRecordStatus.actionType"
      >
        {{ bimiRecordStatus.message }}
        <div v-if="bimiRecordStatus.canAnalyse">
          &nbsp;-
          <LinkDomainAnalysis :domain="result.domain" fragment="bimi" />
        </div>
      </ExplainScorePart>
      <div v-if="result.marketing.has_bimi_record">
        <ExplainScorePart
          :title="
            $t('domain_score.partials.detailed_report.branding.bimi_card.image')
          "
          :action-type="imageMessage.actionType"
        >
          {{ imageMessage.message }}
        </ExplainScorePart>
        <ExplainScorePart
          :title="
            $t(
              'domain_score.partials.detailed_report.branding.bimi_card.certificate'
            )
          "
          :action-type="certificateMessage.actionType"
        >
          {{ certificateMessage.message }}
        </ExplainScorePart>
      </div>
    </div>
  </Card>
</template>
